
import React, { useState, useRef, useMemo, useEffect, useCallback } from 'react';
import { Booth } from '../types';
import { MAP_CONFIG } from '../constants';

interface BoothMapProps {
  booths: Booth[];
  filteredBooths: Set<string>;
  favorites: Set<string>;
  onBoothClick: (booth: Booth) => void;
}

const BoothMap: React.FC<BoothMapProps> = ({ booths, filteredBooths, favorites, onBoothClick }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const [minScale, setMinScale] = useState(0.1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const lastTouch = useRef<{ x: number, y: number } | null>(null);

  // 10px padding from the very edge of the map content
  const PADDING = 10;
  const LABEL_HEIGHT = 50;
  const ENTRANCE_WIDTH = 40;
  const ENTRANCE_HEIGHT = 120;

  // Helper to calculate booth top offset based on specific layout rules
  const getBoothTopOffset = useCallback((row: string, index: number) => {
    let offset = (index - 1) * MAP_CONFIG.gridHeight;
    
    if (row === 'A') {
      if (index > 8) offset += 2 * MAP_CONFIG.gridHeight;
    } else if (['B', 'C', 'F', 'G'].includes(row)) {
      if (index > 12) offset += 2 * MAP_CONFIG.gridHeight;
      if (index > 24) offset += 2 * MAP_CONFIG.gridHeight;
    } else if (['D', 'E'].includes(row)) {
      // Start shift: starts from position 7
      offset += (7 - 1) * MAP_CONFIG.gridHeight;
      if (index > 6) offset += 2 * MAP_CONFIG.gridHeight;
    } else if (row === 'H') {
      // Start shift: starts from position 3
      offset += (3 - 1) * MAP_CONFIG.gridHeight;
      if (index > 10) offset += 2 * MAP_CONFIG.gridHeight;
    }
    
    return offset;
  }, []);

  const { columnOffsets, contentSize } = useMemo(() => {
    const offsets: Record<string, number> = {};
    let currentX = 0;
    let maxBoothY = 0;
    
    // Calculate offsets for each column based on spacings
    for (let i = 0; i < MAP_CONFIG.columns.length; i++) {
      const col = MAP_CONFIG.columns[i];
      offsets[col] = currentX;
      
      const count = MAP_CONFIG.columnCounts[col];
      // Height is the offset of the last booth + its height
      const effectiveHeight = count > 0 ? getBoothTopOffset(col, count) + MAP_CONFIG.gridHeight : 0;
      
      maxBoothY = Math.max(maxBoothY, effectiveHeight);

      const nextCol = MAP_CONFIG.columns[i + 1];
      if (nextCol) {
        const gapKey = `${col}-${nextCol}`;
        const gap = MAP_CONFIG.spacings[gapKey] || 20;
        currentX += MAP_CONFIG.gridWidth + gap;
      } else {
        currentX += MAP_CONFIG.gridWidth;
      }
    }

    // Width: total columns width + entrance area
    // Height: max booth height + label area + some space for entrance position
    const totalWidth = currentX + ENTRANCE_WIDTH + 20;
    
    // Entrance position depends on Row A's height
    const rowAHeight = MAP_CONFIG.columnCounts['A'] > 0 
      ? getBoothTopOffset('A', MAP_CONFIG.columnCounts['A']) + MAP_CONFIG.gridHeight 
      : 0;
    const entranceAHeight = rowAHeight + 50;

    const totalHeight = Math.max(
      maxBoothY + LABEL_HEIGHT, 
      entranceAHeight + ENTRANCE_HEIGHT + 20
    );

    return { 
      columnOffsets: offsets, 
      contentSize: {
        width: totalWidth + (PADDING * 2),
        height: totalHeight + (PADDING * 2)
      }
    };
  }, [getBoothTopOffset]);

  const clampPosition = useCallback((pos: { x: number, y: number }, currentScale: number) => {
    if (!containerRef.current) return pos;
    const { width: containerW, height: containerH } = containerRef.current.getBoundingClientRect();
    
    const scaledW = contentSize.width * currentScale;
    const scaledH = contentSize.height * currentScale;
    
    let minX, maxX, minY, maxY;

    // Horizontal limit
    if (scaledW <= containerW) {
      minX = maxX = (containerW - scaledW) / 2;
    } else {
      maxX = 0;
      minX = containerW - scaledW;
    }

    // Vertical limit
    if (scaledH <= containerH) {
      minY = maxY = (containerH - scaledH) / 2;
    } else {
      maxY = 0;
      minY = containerH - scaledH;
    }

    return {
      x: Math.min(Math.max(pos.x, minX), maxX),
      y: Math.min(Math.max(pos.y, minY), maxY)
    };
  }, [contentSize]);

  useEffect(() => {
    const handleResize = () => {
      if (containerRef.current) {
        const { width, height } = containerRef.current.getBoundingClientRect();
        
        // Calculate the absolute minimum scale to fit content tightly
        const fitScale = Math.min(
          width / contentSize.width, 
          height / contentSize.height
        );
        
        setMinScale(fitScale);
        
        // If current scale is smaller than minScale, reset to minScale
        setScale(prev => {
          const newScale = Math.max(prev, fitScale);
          setPosition(p => clampPosition(p, newScale));
          return newScale;
        });
      }
    };

    handleResize();
    const timer = setTimeout(handleResize, 50);
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timer);
    };
  }, [contentSize, clampPosition]);

  const handleMouseDown = (e: React.MouseEvent) => {
    setIsDragging(true);
    lastTouch.current = { x: e.clientX, y: e.clientY };
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !lastTouch.current) return;
    const dx = e.clientX - lastTouch.current.x;
    const dy = e.clientY - lastTouch.current.y;
    setPosition(prev => clampPosition({ x: prev.x + dx, y: prev.y + dy }, scale));
    lastTouch.current = { x: e.clientX, y: e.clientY };
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      setIsDragging(true);
      lastTouch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 1 && isDragging && lastTouch.current) {
      const dx = e.touches[0].clientX - lastTouch.current.x;
      const dy = e.touches[0].clientY - lastTouch.current.y;
      setPosition(prev => clampPosition({ x: prev.x + dx, y: prev.y + dy }, scale));
      lastTouch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    lastTouch.current = null;
  };

  const handleWheel = (e: React.WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newScale = Math.min(Math.max(scale * delta, minScale), 3);
    setScale(newScale);
    setPosition(prev => clampPosition(prev, newScale));
  };

  return (
    <div 
      ref={containerRef}
      className="relative w-full h-full overflow-hidden bg-white cursor-grab active:cursor-grabbing select-none"
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleMouseUp}
      onWheel={handleWheel}
    >
      <div 
        className="absolute transition-transform duration-75 origin-top-left"
        style={{ 
          width: contentSize.width,
          height: contentSize.height,
          transform: `translate(${position.x}px, ${position.y}px) scale(${scale})` 
        }}
      >
        {/* Actual Content Wrapper with Padding */}
        <div style={{ position: 'absolute', left: PADDING, top: PADDING }}>
          
          {/* Column Labels */}
          {MAP_CONFIG.columns.map(col => (
            <div 
              key={`label-${col}`}
              className="absolute text-center font-black text-black pointer-events-none text-2xl flex items-center justify-center"
              style={{
                left: columnOffsets[col],
                top: 0,
                width: MAP_CONFIG.gridWidth,
                height: LABEL_HEIGHT - 10
              }}
            >
              {col}
            </div>
          ))}

          {/* Entrance / Exit Decorator */}
          <div 
            className="absolute flex items-center justify-center bg-gray-200 text-gray-500 font-black rounded-lg shadow-sm border-2 border-white pointer-events-none select-none"
            style={{
              left: columnOffsets['A'] + MAP_CONFIG.gridWidth + 10,
              top: (MAP_CONFIG.columnCounts['A'] > 0 ? getBoothTopOffset('A', MAP_CONFIG.columnCounts['A']) + MAP_CONFIG.gridHeight : 0) + LABEL_HEIGHT + 10,
              width: 36,
              height: 120,
              writingMode: 'vertical-rl',
              textOrientation: 'upright',
              fontSize: '1.1rem',
              letterSpacing: '0.1em'
            }}
          >
            出入口
          </div>

          {/* Booth Grid */}
          {booths.map(booth => {
            const isHighlighted = filteredBooths.has(booth.id);
            const isFavorite = favorites.has(booth.id);
            const boothTopOffset = getBoothTopOffset(booth.row, booth.index);
            
            // 背景與文字顏色分階
            const getBoothStyle = () => {
              if (isFavorite) return 'bg-orange-500 border-orange-600 text-white shadow-md z-20 hover:bg-orange-600 cursor-pointer hover:z-[200]';
              
              const completeness = booth.completeness || 0;

              // 未被偵測到（搜尋過濾中）
              if (!isHighlighted) {
                return 'bg-gray-100 border-gray-50 text-gray-200 cursor-default z-0';
              }

              // 已被偵測到（高亮狀態）
              let bgClass = 'bg-white';
              let borderClass = 'border-gray-300';
              let textClass = 'text-gray-800';

              if (completeness >= 1) {
                bgClass = 'bg-amber-300';
                borderClass = 'border-amber-400';
                textClass = 'text-amber-900 font-black';
              } else if (completeness >= 0.6) {
                bgClass = 'bg-yellow-200';
                borderClass = 'border-yellow-300';
                textClass = 'text-yellow-900';
              } else if (completeness >= 0.3) {
                bgClass = 'bg-yellow-100';
                borderClass = 'border-yellow-200';
                textClass = 'text-yellow-800';
              }

              return `${bgClass} ${borderClass} ${textClass} shadow-sm z-10 hover:bg-orange-500 hover:text-white hover:border-orange-600 cursor-pointer hover:z-[200] scale-105 font-bold`;
            };

            return (
              <div
                key={booth.id}
                onClick={(e) => {
                  e.stopPropagation();
                  if (booth.completeness > 0 || isHighlighted || isFavorite) onBoothClick(booth);
                }}
                className={`absolute flex items-center justify-center border text-[9px] transition-all group active:scale-95
                  ${getBoothStyle()}
                `}
                style={{
                  left: columnOffsets[booth.row],
                  top: boothTopOffset + LABEL_HEIGHT,
                  width: MAP_CONFIG.gridWidth,
                  height: MAP_CONFIG.gridHeight,
                  opacity: (isHighlighted || isFavorite) ? 1 : (booth.completeness > 0 ? 0.95 : 0.2)
                }}
              >
                {booth.id}
                
                {(isHighlighted || isFavorite || (booth.completeness > 0 && scale > 1.2)) && (
                  <div 
                    className="absolute bottom-full left-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-[100] shadow-2xl border border-white/20"
                    style={{ 
                      transform: `translateX(-50%) scale(${1 / scale})`,
                      transformOrigin: 'bottom center'
                    }}
                  >
                    <div className="font-bold">{booth.circleName}</div>
                    <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 border-[6px] border-transparent border-t-gray-900"></div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
      
      {/* Zoom Controls */}
      <div className="absolute bottom-6 right-6 flex flex-col gap-2 z-40">
        <button 
          onClick={() => {
            const next = Math.min(scale + 0.3, 3);
            setScale(next);
            setPosition(p => clampPosition(p, next));
          }}
          className="w-12 h-12 bg-white shadow-lg border-2 border-gray-100 rounded-2xl flex items-center justify-center text-gray-600 font-bold hover:bg-gray-50 active:scale-90 transition-all text-xl"
        >
          +
        </button>
        <button 
          onClick={() => {
            const next = Math.max(scale - 0.3, minScale);
            setScale(next);
            setPosition(p => clampPosition(p, next));
          }}
          className={`w-12 h-12 bg-white shadow-lg border-2 border-gray-100 rounded-2xl flex items-center justify-center text-gray-600 font-bold hover:bg-gray-50 active:scale-90 transition-all text-xl ${scale <= minScale ? 'opacity-30 pointer-events-none' : ''}`}
        >
          -
        </button>
      </div>
    </div>
  );
};

export default BoothMap;
